import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Pill, Shield, Zap, ArrowLeft, Clock, AlertTriangle, Activity, Circle, Stethoscope, Heart } from 'lucide-react';
import AboutHeader from "./AboutHeader"
import Footer from './landing/Footer';
import Header from './landing/Header';
import SEOHeader from '../components/SEOHeader';


function Medications() {
  const marineBlue = '#1e5f74'; // Dark marine blue color
  const lightBlue = '#5fb3d4'; 
  const navigate = useNavigate();

  const stimulantMeds = [
    {
      name: "Methylphenidate",
      brand: "(Ritalin®, Concerta®)",
      description: "Improves focus and reduces hyperactivity by boosting dopamine/noradrenaline. Available in short- and long-acting forms.",
      details: [
        { label: "Duration", value: "3–4 hrs (immediate), 6–12 hrs (extended)" },
        { label: "Effectiveness", value: "High. Often first-line treatment" },
        { label: "Side Effects", value: "Appetite loss, insomnia, headaches, irritability" },
        { label: "Long-term Safety", value: "Safe when monitored; may slightly slow growth" }
      ],
      safety: {
        risk: "Low when used correctly",
        dangers: "Avoid if heart issues; overdose or misuse is serious"
      }
    },
    {
      name: "Dexamphetamine",
      description: "Also boosts dopamine and noradrenaline, similar to methylphenidate but with a slightly different mechanism.",
      details: [
        { label: "Duration", value: "4–6 hrs (immediate release only)" },
        { label: "Effectiveness", value: "High. Some respond better to this than methylphenidate" },
        { label: "Side Effects", value: "Similar to methylphenidate" },
        { label: "Long-term Safety", value: "Safe under supervision. Tolerance possible" }
      ],
      safety: {
        risk: "Low when used as prescribed",
        dangers: "Same cautions as other stimulants"
      }
    },
    {
      name: "Lisdexamfetamine",
      brand: "(Vyvanse®)",
      description: "A long-acting prodrug of dexamphetamine. Taken once daily, offering smoother effect.",
      details: [
        { label: "Duration", value: "12–14 hrs" },
        { label: "Effectiveness", value: "Very high; preferred for full-day coverage" },
        { label: "Side Effects", value: "Similar to other stimulants, often fewer peaks/troughs" },
        { label: "Long-term Safety", value: "Safe with ongoing monitoring" }
      ],
      safety: {
        risk: "Low due to slow release",
        dangers: "Misuse can be dangerous; monitor cardiovascular and psychiatric symptoms"
      }
    }
  ];

  const nonStimulantMeds = [
    {
      name: "Atomoxetine",
      brand: "(Strattera®)",
      description: "A norepinephrine reuptake inhibitor. Often used when stimulants are not tolerated.",
      details: [
        { label: "Use", value: "ADHD with anxiety/tics or preference for non-controlled meds" },
        { label: "Duration", value: "24 hrs per dose after buildup" },
        { label: "Effectiveness", value: "Moderate to high. Works over weeks" },
        { label: "Side Effects", value: "Drowsiness, nausea, mood swings" },
        { label: "Long-term Safety", value: "Generally safe. Rare liver or mood issues—monitor" }
      ],
      safety: {
        risk: "None",
        dangers: "Monitor for liver issues and suicidal thoughts in youth"
      }
    },
    {
      name: "Guanfacine",
      brand: "(Intuniv®)",
      description: "A selective alpha-2A agonist originally used for blood pressure.",
      details: [
        { label: "Use", value: "For hyperactivity, aggression, or sleep issues; often in children" },
        { label: "Duration", value: "24 hrs" },
        { label: "Effectiveness", value: "Moderate. Good for impulsivity, irritability" },
        { label: "Side Effects", value: "Drowsiness, low blood pressure, dizziness" },
        { label: "Long-term Safety", value: "Safe with monitoring. Don't stop suddenly—tapering needed" }
      ],
      safety: {
        risk: "None",
        dangers: "Risk of rebound hypertension if stopped abruptly. Avoid combining with sedatives"
      }
    }
  ];

  const offLabelMeds = [
    {
      name: "Clonidine",
      description: "Similar to guanfacine. Useful for sleep/tics but sedating. Requires cautious dosing."
    },
    {
      name: "Antidepressants",
      description: "e.g. bupropion, TCAs: Occasionally used in adults with ADHD and comorbid depression. Lower effectiveness; more side effects."
    },
    {
      name: "Modafinil",
      description: "Wakefulness drug. May help attention in adults. Not approved for ADHD; avoid in children."
    },
    {
      name: "Others",
      description: "Risperidone (for aggression), melatonin (sleep), and omega-3 (mild benefit) are sometimes used adjunctively."
    }
  ];

  const safetyReminders = [
    "Never take ADHD meds not prescribed to you",
    "Follow dosing strictly; don't mix or increase without approval",
    "Securely store medications to prevent misuse or accidents",
    "Regular follow-ups are essential for dose checks and side effect monitoring",
    "Medication is most effective when combined with behavioral strategies and support"
  ];

  const getLabelColor = (label) => {
    switch (label) {
      case 'Duration':
        return 'border-blue-500 bg-blue-50 text-blue-700';
      case 'Effectiveness':
        return 'border-green-500 bg-green-50 text-green-700';
      case 'Side Effects':
        return 'border-yellow-500 bg-yellow-50 text-yellow-700';
      case 'Long-term Safety':
        return 'border-purple-500 bg-purple-50 text-purple-700';
      case 'Use':
        return 'border-indigo-500 bg-indigo-50 text-indigo-700';
      default:
        return 'border-gray-500 bg-gray-50 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-white">
         <SEOHeader
            title="ADHD Medications | ADHD Rapid Care Australia"
            description="Learn about ADHD medication options with ADHD Rapid Care. Our clinicians provide safe, personalised treatment plans in Australia, including diagnosis, prescriptions, and ongoing medication management for effective ADHD care."
            path="/medications"
          />



       <Header/>


      {/* Hero Section */}
      <section className=" text-white text-center py-16 px-4" style={{
        '--tw-gradient-from': '#155dfc',
        '--tw-gradient-to': '#009689',
        '--tw-gradient-stops': 'var(--tw-gradient-from), var(--tw-gradient-to)',
        backgroundImage: 'linear-gradient(to right, var(--tw-gradient-stops))'
      }}>
        <Pill className="w-16 h-16 mx-auto mb-6 text-blue-200" />
        <h1 className="text-4xl md:text-5xl font-bold mb-4">ADHD Medications in Australia</h1>
        <p className="text-lg text-blue-100 max-w-4xl mx-auto">
          Comprehensive guide to ADHD medication options—how they work, benefits, side effects, 
          long-term safety, and off-label options. All medications should be used under medical supervision.
        </p>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-16">

        {/* Comparison Section */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Stimulant vs Non-Stimulant Medications
        </h2>

        <div className="grid md:grid-cols-2 gap-8 mb-24">
          {/* Stimulants Card */}
          <div className="bg-blue-50 rounded-2xl p-8 border-l-6  border-l-4 border-blue-600">
            <div className="flex items-center gap-4 mb-6">
              <Zap className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-blue-800">Stimulants</h3>
            </div>
            <p className="text-blue-700 mb-4">
              Fast-acting and first-line for most patients. They increase dopamine and noradrenaline,
              improving focus and impulse control. About 70–80% of children respond well.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-blue-700">
                <span className="text-blue-700">•</span>
                Act quickly but may require multiple daily doses
              </li>
              <li className="flex items-start gap-2 text-blue-700">
                <span className="text-blue-700">•</span>
                Schedule 8 controlled drugs
              </li>
              <li className="flex items-start gap-2 text-blue-700">
                <span className="text-blue-700">•</span>
                Examples: methylphenidate, dexamphetamine, lisdexamfetamine
              </li>
            </ul>
          </div>

          {/* Non-Stimulants Card */}
          <div className="bg-green-50 rounded-2xl p-8 border-l-6 border-l-4 border-green-600 ">
            <div className="flex items-center gap-4 mb-6">
              <Shield className="w-8 h-8 text-green-600" />
              <h3 className="text-2xl font-bold text-green-800">Non-Stimulants</h3>
            </div>
            <p className="text-green-700 mb-4">
              Work differently, act more slowly, and offer 24-hour coverage. Used when stimulants
              are unsuitable or as add-ons.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-green-700">
                <span className="text-green-700">•</span>
                Take weeks to work but offer smoother, lasting control
              </li>
              <li className="flex items-start gap-2 text-green-700">
                <span className="text-green-700">•</span>
                Schedule 4 medications
              </li>
              <li className="flex items-start gap-2 text-green-700">
                <span className="text-green-700">•</span>
                Examples: atomoxetine, guanfacine
              </li>
            </ul>
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Stimulant Medications</h1>

        {/* Methylphenidate Section */}
        <div className="bg-blue-50 rounded-xl p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <Brain className="w-10 h-10 text-blue-600" />
            <div>
              <h3 className="text-2xl font-bold text-blue-800">Methylphenidate</h3>
              <p className="text-blue-600 font-semibold">(Ritalin®, Concerta®)</p>
            </div>
          </div>

          <p className="text-blue-700 mb-6 text-lg">
            Improves focus and reduces hyperactivity by boosting dopamine/noradrenaline. Available in short- and long-acting forms.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Duration</h3>
                <p className="text-blue-700">3-4 hrs (immediate), 6-12 hrs (extended)</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Side Effects</h3>
                <p className="text-blue-700">Appetite loss, insomnia, headaches, irritability</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Activity className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Effectiveness</h3>
                <p className="text-blue-700">High. Often first-line treatment</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Shield className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Long-term Safety</h3>
                <p className="text-blue-700">Safe when monitored; may slightly slow growth</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-100 p-4 rounded">
            <div className="text-blue-800">
              <strong >Addiction risk:</strong> Low when used correctly
            </div>
            <div className="text-blue-800">
              <strong >Dangers:</strong> Avoid if heart issues; overdose or misuse is serious
            </div>
          </div>
        </div>

        {/* Dexamphetamine Section */}
        <div className="bg-blue-50 rounded-xl p-8 mb-16">
          <div className="flex items-start gap-4 mb-6">
            <Activity className="w-10 h-10 text-blue-600" />
            <h2 className="text-2xl font-bold text-blue-800">Dexamphetamine</h2>
          </div>

          <p className="text-blue-700 mb-6 text-lg">
            Also boosts dopamine and noradrenaline, similar to methylphenidate but with a slightly different mechanism.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Duration</h4>
                <p className="text-blue-700">4-6 hrs (immediate release only)</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Side Effects</h4>
                <p className="text-blue-700">Similar to methylphenidate</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Activity className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Effectiveness</h4>
                <p className="text-blue-700">High. Some respond better to this than methylphenidate</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Shield className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Long-term Safety</h4>
                <p className="text-blue-700">Safe under supervision. Tolerance possible</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-100 p-4 rounded">
            <div className="text-blue-800">
              <strong>Addiction risk:</strong> Low when used as prescribed
            </div>
            <div className="text-blue-800">
              <strong>Dangers:</strong> Same cautions as other stimulants
            </div>
          </div>
        </div>

        {/* Lisdexamfetamine Section */}
        <div className="bg-blue-50 rounded-xl p-8 mb-16">
          <div className="flex items-start gap-4 mb-6">
            <Zap className="w-10 h-10 text-blue-600" />
            <div>
              <h3 className="text-2xl font-bold text-blue-800">Lisdexamfetamine</h3>
              <p className="text-blue-600 font-semibold">(Vyvanse®)</p>
            </div>
          </div>

          <p className="text-blue-700 mb-6 text-lg">
            A long-acting prodrug of dexamphetamine. Taken once daily, offering smoother effect.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Duration</h3>
                <p className="text-blue-700">12-14 hrs</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Side Effects</h3>
                <p className="text-blue-700">Similar to other stimulants, often fewer peaks/troughs</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Activity className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Effectiveness</h3>
                <p className="text-blue-700">Very high; preferred for full-day coverage</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Shield className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Long-term Safety</h3>
                <p className="text-blue-700">Safe with ongoing monitoring</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-100 p-4 rounded">
            <div className="text-blue-800">
              <strong >Addiction risk:</strong> Low due to slow release
            </div>
            <div className="text-blue-800">
              <strong >Dangers:</strong> Misuse can be dangerous; monitor cardiovascular and psychiatric symptoms
            </div>
          </div>
        </div>

        {/* Non-Stimulant Medications */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Non-Stimulant Medications</h2>

        {/* Atomoxetine Section */}
        <div className="bg-blue-50 rounded-xl p-8 mb-16">
          <div className="flex items-start gap-4 mb-6">
            <Shield className="w-10 h-10 text-blue-600" />
            <div>
              <h3 className="text-2xl font-bold text-blue-800">Atomoxetine</h3>
              <p className="text-blue-600 font-semibold">(Strattera®)</p>
            </div>
          </div>

          <p className="text-blue-700 mb-6 text-lg">
            A norepinephrine reuptake inhibitor. Often used when stimulants are not tolerated.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-12 mb-6">
            <div className="flex items-start gap-4">
              <Pill className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Use</h3>
                <p className="text-blue-700">ADHD with anxiety/tics or preference for non-controlled meds</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Side Effects</h3>
                 <p className="text-blue-700">Drowsiness, nausea, mood swings</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Duration</h3>
                <p className="text-blue-700">24 hrs per dose after buildup</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Shield className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Long-term Safety</h3>
                <p className="text-blue-700">Generally safe. Rare liver or mood issues—monitor</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Activity className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800">Effectiveness</h3>
                <p className="text-blue-700">Moderate to high. Works over weeks</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-100 p-4 rounded">
            <div className="text-blue-800">
              <strong >Addiction risk:</strong> None
            </div>
            <div className="text-blue-800">
              <strong >Dangers:</strong> Monitor for liver issues and suicidal thoughts in youth
            </div>
          </div>
        </div>

        {/* Guanfacine Section */}
        <div className="bg-blue-50 rounded-xl p-8">
          <div className="flex items-start gap-4 mb-6">
            <Heart className="w-10 h-10 text-blue-600" />
            <div>
              <h3 className="text-2xl font-bold text-blue-800">Guanfacine</h3>
              <p className="text-blue-600 font-semibold">(Intuniv®)</p>
            </div>
          </div>

          <p className="text-blue-700 mb-6 text-lg">
            A selective alpha-2A agonist originally used for blood pressure.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-12 mb-6">
            <div className="flex items-start gap-4">
              <Pill className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Use</h4>
                <p className="text-blue-700">For hyperactivity, aggression, or sleep issues; often in children</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Side Effects</h4>
                <p className="text-blue-700">Drowsiness, low blood pressure, dizziness</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Duration</h4>
                <p className="text-blue-700">24 hrs</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
               <Shield className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Long-term Safety</h4>
                <p className="text-blue-700">Safe with monitoring. Don't stop suddenly—tapering needed</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Activity className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-bold text-blue-800">Effectiveness</h4>
                <p className="text-blue-700">Moderate. Good for impulsivity, irritability</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-100 p-4 rounded">
            <div className="text-blue-800">
              <strong >Addiction risk:</strong> None
            </div>
            <div className="text-blue-800">
              <strong>Dangers:</strong> Risk of rebound hypertension if stopped abruptly. Avoid combining with sedatives
            </div>
          </div>
        </div>

        {/* Off-Label Medications Section */}
        <div className="mt-12 bg-white shadow-lg p-8 rounded-xl">
          <div className="flex items-center gap-4 mb-6">
            <Pill className="w-8 h-8 text-orange-600" />
            <h2 className="text-3xl font-bold text-gray-900">Off-Label Medications</h2>
          </div>
          <p className="text-lg text-gray-700 mb-8">Used when first-line medications fail or cause side effects.</p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Clonidine */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-orange-800 mb-3">Clonidine</h3>
              <p className="text-orange-700">Similar to guanfacine. Useful for sleep/tics but sedating. Requires cautious dosing.</p>
            </div>

            {/* Modafinil */}
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-800 mb-3">Modafinil</h3>
              <p className="text-green-700">Wakefulness drug. May help attention in adults. Not approved for ADHD; avoid in children.</p>
            </div>

            {/* Antidepressants */}
            <div className="bg-purple-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-purple-800 mb-3">Antidepressants</h3>
              <p className="text-purple-700">e.g. bupropion, TCAs: Occasionally used in adults with ADHD and comorbid depression. Lower effectiveness; more side effects.</p>
            </div>

            {/* Others */}
            <div className=" rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Others</h3>
              <p className="text-gray-700">Risperidone (for aggression), melatonin (sleep), and omega-3 (mild benefit) are sometimes used adjunctively.</p>
            </div>
          </div>
        </div>

        {/* Safety Reminders Section */}
        <div className="mt-12 relative ">
          {/* Red vertical line */}
          {/* <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600 rounded-lg"></div> */}
          
          <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-8">
            <div className="flex items-center gap-2 mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <h2 className="text-3xl font-bold text-red-800">Safety Reminders</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div class="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <span className="text-red-600">Never take ADHD meds not prescribed to you</span>
                </div>
                <div className="flex items-start gap-4">
                  <div class="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <span className="text-red-600">Follow dosing strictly; don't mix or increase without approval</span>
                </div>
                <div className="flex items-start gap-4">
                 <div class="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <span className="text-red-600">Securely store medications to prevent misuse or accidents</span>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div class="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <span className="text-red-600">Regular follow-ups are essential for dose checks and side effect monitoring</span>
                </div>
                <div className="flex items-start gap-4">
                  <div class="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                  <span className="text-red-600">Medication is most effective when combined with behavioral strategies and support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expert Guidance CTA */}
        <div className="mt-12 rounded-2xl overflow-hidden" >
          <div className=" text-white py-12 px-8 text-center" style={{
            '--tw-gradient-from': '#155dfc',
            '--tw-gradient-to': '#009689',
            '--tw-gradient-stops': 'var(--tw-gradient-from), var(--tw-gradient-to)',
            backgroundImage: 'linear-gradient(to right, var(--tw-gradient-stops))'
          }}>
            <h2 className="text-3xl font-bold mb-4">Need Expert Medication Guidance and Ongoing Care?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Our qualified ADHD trained nurses and professionals provide 
              comprehensive medication consultations and monitoring as part of 
              our ongoing AI enhanced, ADHD Care Plan.
            </p>
            <div className="flex justify-center gap-4">
              {/* <button 
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                onClick={() => navigate('/assessment')}
              >
                Start Your Assessment
              </button> */}
              <button 
                className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                onClick={() => navigate('/care-plan')}
              >
                View details HERE
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer/>
    </div>
  );
}

export default Medications; 